import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["dialog", "choice", "chapters", "error", "completed", "chapter"];
  static values = { prompt: Boolean, csrf: String, scope: String };

  declare readonly dialogTarget: HTMLDialogElement;
  declare readonly choiceTarget: HTMLElement;
  declare readonly chaptersTarget: HTMLElement;
  declare readonly errorTarget: HTMLElement;
  declare readonly completedTarget: HTMLElement;
  declare readonly chapterTargets: HTMLButtonElement[];
  declare readonly csrfValue: string;
  declare readonly scopeValue: string;
  declare promptValue: boolean;
  private busy = false;
  private opener: HTMLElement | null = null;

  connect() {
    if (document.documentElement.hasAttribute("data-turbo-preview")) return;
    if (!this.promptValue) return;
    let active = false;
    try {
      const saved = JSON.parse(sessionStorage.getItem("walkthrough_active_tour") || "null");
      active = saved?.scope === this.scopeValue && !!saved?.tourId && !saved?.paused;
    } catch { /* Invalid saved progress must not hide the first-visit choice. */ }
    if (!active) this.show(true);
  }

  disconnect() {
    this.dialogTarget.close();
  }

  open() {
    this.show(false);
  }

  async choose(event: Event) {
    const choice = (event.currentTarget as HTMLElement).dataset.choice!;
    if (this.busy) return;
    this.setBusy(true);
    this.errorTarget.hidden = true;
    try {
      const response = await fetch("/walkthrough/choice", {
        method: "PATCH",
        headers: { "Content-Type": "application/json", "X-CSRF-Token": this.csrfValue },
        body: JSON.stringify({ choice }),
      });
      if (!response.ok) throw new Error("Choice was not saved");
      this.promptValue = false;
      if (choice === "deep_dive") this.show(false);
      else {
        this.close();
        if (choice === "basics") this.launch("getting_started");
        else window.dispatchEvent(new CustomEvent("walkthrough:introduction-dismissed"));
      }
    } catch {
      this.errorTarget.hidden = false;
    } finally {
      this.setBusy(false);
    }
  }

  start(event: Event) {
    const id = (event.currentTarget as HTMLElement).dataset.tourId;
    if (id) this.launch(id);
  }

  finished(event: CustomEvent) {
    const index = this.chapterTargets.findIndex(button => button.dataset.tourId === event.detail.tourId);
    if (index < 0) return;
    const chapter = this.chapterTargets[index];
    chapter.querySelector<HTMLElement>("[data-chapter-completed]")!.hidden = false;
    const next = this.chapterTargets[index + 1];
    this.completedTarget.hidden = false;
    if (next) {
      this.launch(next.dataset.tourId!, false);
      return;
    }
    this.show(false);
  }

  failed() {
    this.show(false);
    this.errorTarget.hidden = false;
  }

  cancel(event: Event) {
    if (this.promptValue || this.busy) event.preventDefault();
  }

  close() {
    this.dialogTarget.close();
    this.opener?.focus({ preventScroll: true });
  }

  private launch(tourId: string, resume = true) {
    this.close();
    this.completedTarget.hidden = true;
    window.dispatchEvent(new CustomEvent("walkthrough:start-tour", { detail: { tourId, resume } }));
  }

  private show(choice: boolean) {
    this.choiceTarget.hidden = !choice;
    this.chaptersTarget.hidden = choice;
    if (!this.dialogTarget.open) {
      this.opener = document.activeElement as HTMLElement;
      this.dialogTarget.showModal();
    }
  }

  private setBusy(busy: boolean) {
    this.busy = busy;
    this.dialogTarget.setAttribute("aria-busy", String(busy));
    this.choiceTarget.querySelectorAll<HTMLButtonElement>("button").forEach(button => button.disabled = busy);
  }
}
